// Flèches de navigation dans le HEADER d'un élément (scène, chat, fiche, quiz, tâche).
//
// Remplace les barres d'actions de bas d'écran : le header est déjà fixe, donc les flèches sont
// TOUJOURS visibles sans dépendre de la chaîne flex — un pied de page, lui, ne tient que si
// chaque maillon est borné (piège qui a coûté deux itérations, cf. app/utils/cyb).
// Un bouton sans handler est rendu DÉSACTIVÉ (grisé, non cliquable) plutôt que masqué :
// la position des flèches reste stable d'un écran à l'autre.
import { useMemo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import { type Palette } from '../utils/cyb';
import { usePalette } from '../hooks/usePalette';
import { useT } from '../i18n/useT';

interface Props {
  onPrev?: () => void;
  onNext?: () => void;
  /** Accent de l'élément (TYPE_COLOR) : c'est lui qui colore la flèche « suivant » active. */
  accent?: string;
  /** Dernier élément/étape : la flèche « suivant » vaut alors « terminer ». */
  isLast?: boolean;
  nextLabel?: string;
  prevLabel?: string;
}

export default function HeaderNav({ onPrev, onNext, accent, isLast = false,
  nextLabel, prevLabel }: Props) {
  const t = useT();
  const next = nextLabel ?? t('nav.next');
  const prev = prevLabel ?? t('nav.previous');
  const C = usePalette();
  const s = useMemo(() => makeStyles(C), [C]);
  // ⚠️ LE DÉFAUT SE RÉSOUT ICI, PAS DANS LA SIGNATURE. Un `accent = C.cyan` en paramètre est
  // évalué AVANT la lecture de la palette — donc avant que `C` existe.
  const nextColor = isLast ? C.green : (accent ?? C.cyan);
  return (
    <View style={s.row}>
      <TouchableOpacity
        accessibilityRole="button"
        accessibilityLabel={prev}
        onPress={onPrev}
        disabled={!onPrev}
        style={[s.btn, { borderColor: onPrev ? C.border : 'transparent' }]}
      >
        <Ionicons name="chevron-back" size={18} color={onPrev ? C.textSec : C.textMute} />
      </TouchableOpacity>
      <TouchableOpacity
        accessibilityRole="button"
        accessibilityLabel={isLast ? 'Terminer' : next}
        onPress={onNext}
        disabled={!onNext}
        style={[s.btn, onNext ? { borderColor: nextColor, backgroundColor: `${nextColor}1F` } : { borderColor: 'transparent' }]}
      >
        <Ionicons
          name={isLast ? 'checkmark' : 'chevron-forward'}
          size={18}
          color={onNext ? nextColor : C.textMute}
        />
      </TouchableOpacity>
    </View>
  );
}

const makeStyles = (C: Palette) => StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 6, flexShrink: 0 },
  btn: { width: 34, height: 34, borderRadius: 17, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
});
